import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import {
  Bike,
  Check,
  Copy,
  Eye,
  Globe2,
  Handshake,
  KeyRound,
  Link2,
  Pencil,
  Plus,
  Power,
  Search,
  Store,
  Users,
} from "lucide-react";
import { Button, FormModal, SearchBar, TableFlat } from "@veorest/ui";
import {
  createAdminNetworkDriver,
  fetchAdminClients,
  fetchAdminDriver,
  fetchAdminDrivers,
  fetchAdminDriverUpdateRequests,
  linkAdminDriverRestaurant,
  mintAdminDriverActivationCode,
  resolveAdminDriverUpdateRequest,
  setAdminDriverActive,
  unlinkAdminDriverRestaurant,
  updateAdminDriver,
  uploadAdminDriverKyc,
} from "@veorest/api-client";
import { PageHeader } from "../components/shell/PageHeader";
import { AdminPager, AdminPanel } from "../components/shell/AdminPanel";
import { StatsStrip } from "../components/data/StatsStrip";
import { SegmentStrip } from "../components/data/SegmentStrip";
import { EmptyState } from "../components/data/EmptyState";
import { formatDate, formatNumber, statusPillClass } from "../lib/format";
import { compressKycImage } from "../lib/kycImage";
import { fleetOwnershipLabel } from "../lib/platformModel";

type FleetFilter = "" | "restaurant" | "oelox_network" | "partner";
type DriverRow = Record<string, unknown> & { id: string };

function driverLabel(row: Record<string, unknown>): string {
  return String(row.display_name || row.driver_name || row.name || "Driver");
}

function mapCreateError(raw: string): string {
  const text = String(raw || "").trim();
  const lower = text.toLowerCase();
  if (text.includes("phone_taken") || lower.includes("already exists")) {
    return "That phone number is already used by another driver.";
  }
  if (text.includes("invalid_phone")) {
    return "Enter a valid phone number with digits only (country code allowed).";
  }
  if (text.includes("image_too_large")) {
    return "Each image must be 8 MB or smaller.";
  }
  if (text.includes("image_type_invalid") || text.includes("image_invalid")) {
    return "Use a JPEG, PNG, or WebP image for IC and license.";
  }
  if (text.includes("kyc_bucket_missing") || (lower.includes("bucket") && lower.includes("not found"))) {
    return "KYC storage is not set up (driver-kyc bucket). Apply the global driver migrations, then retry.";
  }
  if (text.includes("storage_not_configured")) {
    return "File storage is not configured on the API.";
  }
  if (text.includes("not_platform_admin")) {
    return "Your account is not a Super Admin.";
  }
  if (text.includes("plate_required")) return "Vehicle plate / number is required.";
  if (text.includes("ic_image_required")) return "IC image is required.";
  if (text.includes("license_image_required")) return "License image is required.";
  if (text.includes("name_and_phone_required")) return "Name and phone are required.";
  if (text.includes("kyc_storage_denied") || lower.includes("row-level security")) {
    return "KYC upload was blocked. Apply SQL 073 (driver-kyc admin storage), then retry.";
  }
  if (text.includes("rpc_missing") || (lower.includes("function") && lower.includes("does not exist"))) {
    return "Database function missing. Apply Super Admin driver migrations (066–068), then retry.";
  }
  if (lower.includes("failed to fetch") || lower.includes("err_http2")) {
    return "Upload was blocked by the API (file too large or route missing). Retry after applying SQL 073; images are compressed automatically.";
  }
  return text || "Could not create network driver.";
}

function FileField({
  label,
  required,
  file,
  onChange,
}: {
  label: string;
  required?: boolean;
  file: File | null;
  onChange: (file: File | null) => void;
}) {
  return (
    <label className="admin-field">
      <span className="admin-field__label">
        {label}
        {required ? " *" : ""}
      </span>
      <span className="admin-file">
        <span className="btn-secondary relative overflow-hidden cursor-pointer">
          {file ? "Change file" : "Choose image"}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="absolute inset-0 cursor-pointer opacity-0"
            onChange={(e) => onChange(e.target.files?.[0] ?? null)}
          />
        </span>
        <span className="admin-file__name">
          {file ? file.name : "JPEG, PNG, or WebP · max 8 MB"}
        </span>
      </span>
    </label>
  );
}

function DetailLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-[var(--border-subtle)] pb-1.5 text-sm">
      <span className="font-semibold text-[var(--text-muted)]">{label}</span>
      <span className="text-right font-bold">{value}</span>
    </div>
  );
}

export default function DriversPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [fleetFilter, setFleetFilter] = useState<FleetFilter>("");
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [vehicleType, setVehicleType] = useState("motorcycle");
  const [vehicleNumber, setVehicleNumber] = useState("");
  const [vehicleModel, setVehicleModel] = useState("");
  const [collectsCod, setCollectsCod] = useState(true);
  const [icFile, setIcFile] = useState<File | null>(null);
  const [licenseFile, setLicenseFile] = useState<File | null>(null);
  const [homeIdFile, setHomeIdFile] = useState<File | null>(null);
  const [activationCode, setActivationCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [linkDriverId, setLinkDriverId] = useState<string | null>(null);
  const [linkTenantId, setLinkTenantId] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [requestNote, setRequestNote] = useState("");
  const [detailDriverId, setDetailDriverId] = useState<string | null>(null);
  const [editDriverId, setEditDriverId] = useState<string | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    name: "",
    phone: "",
    email: "",
    address: "",
    vehicle_type: "motorcycle",
    vehicle_number: "",
    vehicle_model: "",
    vehicle_color: "",
    collects_cod: true,
    is_verified: false,
  });

  const drivers = useQuery({
    queryKey: ["admin-drivers", search, page, fleetFilter],
    queryFn: () =>
      fetchAdminDrivers({
        search,
        page,
        limit: 25,
        fleetType: fleetFilter || undefined,
      }),
  });

  const clients = useQuery({
    queryKey: ["admin-clients-lite"],
    queryFn: () => fetchAdminClients({ page: 1, limit: 100 }),
    enabled: !!linkDriverId,
  });

  const missingCreateFields = (): string[] => {
    const missing: string[] = [];
    if (!name.trim()) missing.push("full name");
    if (!phone.trim()) missing.push("phone");
    if (!vehicleNumber.trim()) missing.push("plate / vehicle number");
    if (!icFile) missing.push("IC image");
    if (!licenseFile) missing.push("license image");
    return missing;
  };

  const createMut = useMutation({
    mutationFn: async () => {
      const missing = missingCreateFields();
      if (missing.length) throw new Error(`Required: ${missing.join(", ")}`);
      const ic = await compressKycImage(icFile!);
      const license = await compressKycImage(licenseFile!);
      const icUp = await uploadAdminDriverKyc({ kind: "ic", file: ic });
      const licenseUp = await uploadAdminDriverKyc({ kind: "license", file: license });
      let homeIdImageUrl: string | null = null;
      if (homeIdFile) {
        const home = await compressKycImage(homeIdFile);
        const homeUp = await uploadAdminDriverKyc({ kind: "home_id", file: home });
        homeIdImageUrl = homeUp.url;
      }
      return createAdminNetworkDriver({
        name: name.trim(),
        phone: phone.trim(),
        vehicleType,
        vehicleNumber: vehicleNumber.trim(),
        vehicleModel: vehicleModel.trim() || null,
        icImageUrl: icUp.url,
        licenseImageUrl: licenseUp.url,
        homeIdImageUrl,
        collectsCod,
      });
    },
    onSuccess: async (res) => {
      setActivationCode(res.activation_code);
      setFormError(null);
      setName("");
      setPhone("");
      setVehicleNumber("");
      setVehicleModel("");
      setIcFile(null);
      setLicenseFile(null);
      setHomeIdFile(null);
      setCopied(false);
      toast.success("Network driver created. Copy the one-time activation code now.");
      await qc.invalidateQueries({ queryKey: ["admin-drivers"] });
    },
    onError: (e: Error) => {
      const message = mapCreateError(e.message);
      setFormError(message);
      toast.error(message);
    },
  });

  const linkMut = useMutation({
    mutationFn: () => linkAdminDriverRestaurant(linkDriverId!, linkTenantId),
    onSuccess: async () => {
      setLinkDriverId(null);
      setLinkTenantId("");
      toast.success("Driver linked to client.");
      await qc.invalidateQueries({ queryKey: ["admin-drivers"] });
    },
    onError: (e: Error) => toast.error(e.message || "Could not link client"),
  });

  const requests = useQuery({
    queryKey: ["admin-driver-update-requests"],
    queryFn: () => fetchAdminDriverUpdateRequests({ status: "pending", limit: 50 }),
  });

  const detail = useQuery({
    queryKey: ["admin-driver", detailDriverId],
    queryFn: () => fetchAdminDriver(detailDriverId!),
    enabled: !!detailDriverId,
  });
  const detailDriver = (detail.data?.driver ?? null) as Record<string, unknown> | null;

  const activeMut = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => setAdminDriverActive(id, active),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["admin-drivers"] });
      if (detailDriverId) await qc.invalidateQueries({ queryKey: ["admin-driver", detailDriverId] });
    },
    onError: (e: Error) => toast.error(e.message || "Could not update driver"),
  });

  const resolveMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: "approved" | "rejected" }) =>
      resolveAdminDriverUpdateRequest(id, status, requestNote.trim() || undefined),
    onSuccess: async () => {
      setRequestNote("");
      await qc.invalidateQueries({ queryKey: ["admin-driver-update-requests"] });
      await qc.invalidateQueries({ queryKey: ["admin-drivers"] });
    },
  });

  const editMut = useMutation({
    mutationFn: () =>
      updateAdminDriver(editDriverId!, {
        name: editForm.name.trim(),
        phone: editForm.phone.trim(),
        email: editForm.email.trim() || null,
        address: editForm.address.trim() || null,
        vehicleType: editForm.vehicle_type,
        vehicleNumber: editForm.vehicle_number.trim() || null,
        vehicleModel: editForm.vehicle_model.trim() || null,
        vehicleColor: editForm.vehicle_color.trim() || null,
        collectsCod: editForm.collects_cod,
        isVerified: editForm.is_verified,
      }),
    onSuccess: async () => {
      toast.success("Driver updated");
      setEditDriverId(null);
      await qc.invalidateQueries({ queryKey: ["admin-drivers"] });
      if (detailDriverId) await qc.invalidateQueries({ queryKey: ["admin-driver", detailDriverId] });
    },
    onError: (e: Error) => toast.error(mapCreateError(e.message)),
  });

  const mintMut = useMutation({
    mutationFn: (id: string) => mintAdminDriverActivationCode(id),
    onSuccess: async (res, id) => {
      const code = String(res.passcode ?? "");
      if (code) {
        await navigator.clipboard.writeText(code).catch(() => undefined);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toast.success("Activation code generated and copied");
      } else {
        toast.success("Activation code generated");
      }
      await qc.invalidateQueries({ queryKey: ["admin-driver", id] });
    },
    onError: (e: Error) => toast.error(mapCreateError(e.message)),
  });

  const unlinkMut = useMutation({
    mutationFn: ({ driverId, tenantId }: { driverId: string; tenantId: string }) =>
      unlinkAdminDriverRestaurant(driverId, tenantId),
    onSuccess: async () => {
      toast.success("Client link removed");
      if (detailDriverId) await qc.invalidateQueries({ queryKey: ["admin-driver", detailDriverId] });
      await qc.invalidateQueries({ queryKey: ["admin-drivers"] });
    },
    onError: (e: Error) => toast.error(e.message || "Could not unlink client"),
  });

  useEffect(() => {
    if (!editDriverId || !detailDriver) return;
    if (String(detailDriver.id) !== editDriverId) return;
    setEditForm({
      name: String(detailDriver.name ?? ""),
      phone: String(detailDriver.phone ?? ""),
      email: String(detailDriver.email ?? detailDriver.profile_email ?? ""),
      address: String(detailDriver.address ?? ""),
      vehicle_type: String(detailDriver.vehicle_type ?? "motorcycle"),
      vehicle_number: String(detailDriver.vehicle_number ?? detailDriver.vehicle_plate ?? ""),
      vehicle_model: String(detailDriver.vehicle_model ?? ""),
      vehicle_color: String(detailDriver.vehicle_color ?? ""),
      collects_cod: Boolean(detailDriver.collects_cod),
      is_verified: Boolean(detailDriver.is_verified),
    });
  }, [editDriverId, detailDriver]);

  const openEdit = (id: string) => {
    setDetailDriverId(id);
    setEditDriverId(id);
  };

  const totalPages = Math.max(1, Math.ceil((drivers.data?.total ?? 0) / 25));
  const summary = drivers.data?.summary;
  const items = (drivers.data?.items ?? []) as DriverRow[];
  const tenantOptions = useMemo(
    () => (clients.data?.items ?? []) as Array<{ id: string; name?: string }>,
    [clients.data]
  );

  const openCreate = () => {
    setShowCreate(true);
    setActivationCode(null);
    setFormError(null);
    setCopied(false);
  };

  const closeCreate = () => {
    if (activationCode) return;
    setShowCreate(false);
    setFormError(null);
  };

  return (
    <div className="menu-page">
      <PageHeader
        eyebrow="Logistics"
        title="Drivers"
        subtitle="Three fleets: OELOX global (platform), Client drivers (business-owned), and Partner drivers (logistics-owned)."
        actions={
          <Button type="button" icon={<Plus />} onClick={openCreate}>
            Add OELOX global driver
          </Button>
        }
      />

      <StatsStrip
        columns={4}
        loading={drivers.isLoading && !drivers.data}
        tiles={[
          {
            key: "total",
            label: "Total drivers",
            value: formatNumber(summary?.total ?? 0),
            icon: Bike,
            tone: "ink",
            caption: `${formatNumber(summary?.active ?? 0)} online now`,
          },
          {
            key: "network",
            label: "OELOX global",
            value: formatNumber(summary?.network ?? 0),
            icon: Globe2,
            tone: "accent",
            caption: "Platform fleet — own + cover trips",
          },
          {
            key: "partner",
            label: "Partner drivers",
            value: formatNumber(summary?.partner ?? 0),
            icon: Handshake,
            tone: "info",
            caption: "Logistics-owned fleets",
          },
          {
            key: "client",
            label: "Client drivers",
            value: formatNumber(summary?.client ?? summary?.restaurant ?? 0),
            icon: Store,
            tone: "success",
            caption: "Business-owned fleets",
          },
        ]}
      />

      {(requests.data?.items?.length ?? 0) > 0 && (
        <AdminPanel className="mt-4" toolbar={<strong>Pending profile update requests</strong>}>
          <div className="mb-3">
            <input
              className="input-base"
              placeholder="Optional note for approve/reject"
              value={requestNote}
              onChange={(e) => setRequestNote(e.target.value)}
            />
          </div>
          <div className="space-y-3">
            {(requests.data?.items ?? []).map((row) => (
              <div key={String(row.id)} className="flex flex-wrap items-start justify-between gap-2 border-b border-[var(--border)] pb-3">
                <div className="min-w-0">
                  <p className="admin-row-title">{String(row.driver_name || "Driver")}</p>
                  <p className="admin-row-sub">{String(row.driver_phone || "—")}</p>
                  <p className="mt-1 text-sm">{String(row.message || "")}</p>
                  <p className="text-[var(--text-muted)] text-xs">{formatDate(String(row.created_at))}</p>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    className="btn-sm"
                    disabled={resolveMut.isPending}
                    onClick={() => resolveMut.mutate({ id: String(row.id), status: "approved" })}
                  >
                    Approve
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    className="btn-sm"
                    disabled={resolveMut.isPending}
                    onClick={() => resolveMut.mutate({ id: String(row.id), status: "rejected" })}
                  >
                    Reject
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </AdminPanel>
      )}

      {detailDriverId && (
        <AdminPanel
          className="mt-4"
          toolbar={
            <div className="flex w-full flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <strong>{detailDriver ? String(detailDriver.name ?? "Driver") : "Driver details"}</strong>
                {detailDriver ? (
                  <span className={statusPillClass(detailDriver.is_active ? "online" : "offline")}>
                    {String(detailDriver.fleet_type) === "oelox_network" && !detailDriver.activated_at && !detailDriver.is_active
                      ? "Pending code"
                      : detailDriver.is_active
                        ? "Activated"
                        : "Inactive"}
                  </span>
                ) : null}
                {detailDriver ? (
                  <span className={statusPillClass(detailDriver.is_online ? "online" : "offline")}>
                    {detailDriver.is_online ? "Online" : "Offline"}
                  </span>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-2">
                {detailDriver ? (
                  <Button
                    type="button"
                    variant="secondary"
                    className="btn-sm"
                    icon={<Pencil className="h-3.5 w-3.5" />}
                    onClick={() => setEditDriverId(String(detailDriver.id))}
                  >
                    Edit
                  </Button>
                ) : null}
                {detailDriver ? (
                  <Button
                    type="button"
                    variant="secondary"
                    className="btn-sm"
                    icon={<Power className="h-3.5 w-3.5" />}
                    disabled={activeMut.isPending}
                    onClick={() => {
                      const id = String(detailDriver.id);
                      if (detailDriver.is_active) setDeactivateTarget(id);
                      else activeMut.mutate({ id, active: true });
                    }}
                  >
                    {detailDriver.is_active ? "Deactivate" : "Activate"}
                  </Button>
                ) : null}
                <Button type="button" variant="ghost" className="btn-sm" onClick={() => setDetailDriverId(null)}>
                  Close
                </Button>
              </div>
            </div>
          }
        >
          {detail.isLoading ? (
            <p className="text-sm text-[var(--text-muted)]">Loading driver…</p>
          ) : detail.error ? (
            <p className="text-sm text-red-600">{(detail.error as Error).message}</p>
          ) : detailDriver ? (
            <div className="grid gap-5 lg:grid-cols-3">
              <section className="space-y-1.5">
                <p className="text-sm font-bold">Identity</p>
                <DetailLine label="Phone" value={String(detailDriver.phone ?? "—")} />
                <DetailLine label="Email" value={String(detailDriver.email ?? detailDriver.profile_email ?? "—")} />
                <DetailLine label="Fleet" value={fleetOwnershipLabel(detailDriver.fleet_type)} />
                <DetailLine
                  label="Owner / home"
                  value={
                    String(detailDriver.fleet_type) === "partner"
                      ? String(detailDriver.partner_name || "Partner fleet")
                      : String(detailDriver.fleet_type) === "oelox_network"
                        ? "OELOX global"
                        : String(detailDriver.client_name || "Client")
                  }
                />
                <DetailLine label="Joined" value={formatDate(String(detailDriver.created_at))} />
                <DetailLine
                  label="Rating"
                  value={`${Number(detailDriver.rating ?? 5).toFixed(1)} · ${formatNumber(Number(detailDriver.completed_deliveries ?? 0))} deliveries`}
                />
              </section>

              <section className="space-y-1.5">
                <p className="text-sm font-bold">Vehicle & flags</p>
                <DetailLine label="Type" value={String(detailDriver.vehicle_type ?? "—")} />
                <DetailLine label="Plate / number" value={String(detailDriver.vehicle_number ?? detailDriver.vehicle_plate ?? "—")} />
                <DetailLine
                  label="Model / color"
                  value={[detailDriver.vehicle_model, detailDriver.vehicle_color].filter(Boolean).join(" · ") || "—"}
                />
                <DetailLine label="Collects COD" value={detailDriver.collects_cod ? "Yes" : "No"} />
                <DetailLine label="KYC verified" value={detailDriver.is_verified ? "Verified" : "Not verified"} />
                <DetailLine
                  label="Client links"
                  value={formatNumber((detail.data?.links ?? []).filter((l) => String(l.status) === "active").length)}
                />
              </section>

              {String(detailDriver.fleet_type) === "oelox_network" ? (
                <section className="space-y-2">
                  <p className="text-sm font-bold">Activation</p>
                  {detailDriver.activated_at ? (
                    <p className="text-sm text-[var(--text-muted)]">
                      Activated {formatDate(String(detailDriver.activated_at))}
                    </p>
                  ) : (
                    <p className="text-sm text-[var(--text-muted)]">
                      Not activated yet — driver must enter a code in the Driver app.
                    </p>
                  )}
                  {detail.data?.activation_code ? (
                    <div className="space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <code className="rounded-lg bg-[var(--surface-muted)] px-3 py-1.5 font-mono text-base font-black tracking-[0.2em]">
                          {String(detail.data.activation_code)}
                        </code>
                        <Button
                          type="button"
                          variant="secondary"
                          className="btn-sm"
                          icon={copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                          onClick={async () => {
                            await navigator.clipboard.writeText(String(detail.data?.activation_code ?? ""));
                            setCopied(true);
                            setTimeout(() => setCopied(false), 2000);
                          }}
                        >
                          {copied ? "Copied" : "Copy"}
                        </Button>
                      </div>
                      {detail.data.activation_expires_at ? (
                        <p className="text-xs text-[var(--text-muted)]">
                          Expires {formatDate(String(detail.data.activation_expires_at))}
                        </p>
                      ) : null}
                    </div>
                  ) : (
                    <p className="text-sm text-[var(--text-muted)]">No unused activation code.</p>
                  )}
                  <Button
                    type="button"
                    variant="secondary"
                    className="btn-sm"
                    icon={<KeyRound className="h-3.5 w-3.5" />}
                    disabled={mintMut.isPending}
                    onClick={() => mintMut.mutate(String(detailDriver.id))}
                  >
                    {mintMut.isPending ? "Generating…" : "Generate new code"}
                  </Button>
                </section>
              ) : null}

              <section className="lg:col-span-3">
                <p className="mb-2 text-sm font-bold">KYC documents</p>
                <div className="grid gap-3 sm:grid-cols-3">
                  {([
                    ["IC image", detailDriver.ic_image_url],
                    ["License", detailDriver.license_image_url],
                    ["Home ID", detailDriver.home_id_image_url],
                  ] as Array<[string, unknown]>).map(([label, url]) =>
                    url ? (
                      <a
                        key={label}
                        href={String(url)}
                        target="_blank"
                        rel="noreferrer"
                        className="group relative block overflow-hidden rounded-xl border border-[var(--border-subtle)]"
                      >
                        <img
                          src={String(url)}
                          alt={label}
                          loading="lazy"
                          className="h-28 w-full object-cover transition-transform group-hover:scale-[1.03]"
                        />
                        <span className="absolute inset-x-0 bottom-0 bg-black/55 px-2 py-1 text-[11px] font-bold text-white">
                          {label} ↗
                        </span>
                      </a>
                    ) : (
                      <div
                        key={label}
                        className="flex h-28 items-center justify-center rounded-xl border border-dashed border-[var(--border-subtle)] text-xs font-semibold text-[var(--text-muted)]"
                      >
                        {label} — not uploaded
                      </div>
                    )
                  )}
                </div>
              </section>

              <section className="space-y-2 lg:col-span-2">
                <p className="text-sm font-bold">Client links</p>
                {(detail.data?.links ?? []).length === 0 ? (
                  <p className="text-sm text-[var(--text-muted)]">No restaurant links.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {(detail.data?.links ?? []).slice(0, 8).map((l) => (
                      <li
                        key={String(l.id)}
                        className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--border-subtle)] pb-1.5 text-sm"
                      >
                        <span className="font-bold">{String(l.tenant_name || l.tenant_id)}</span>
                        <span className="flex items-center gap-2">
                          <span className={statusPillClass(String(l.status) === "active" ? "online" : "offline")}>
                            {String(l.status)}
                          </span>
                          {String(l.status) === "active" ? (
                            <Button
                              type="button"
                              variant="ghost"
                              className="btn-sm"
                              disabled={unlinkMut.isPending}
                              onClick={() =>
                                unlinkMut.mutate({ driverId: String(detailDriver.id), tenantId: String(l.tenant_id) })
                              }
                            >
                              Unlink
                            </Button>
                          ) : null}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section className="space-y-2">
                <p className="text-sm font-bold">Recent sessions</p>
                {(detail.data?.sessions ?? []).length === 0 ? (
                  <p className="text-sm text-[var(--text-muted)]">No sessions</p>
                ) : (
                  <ul className="space-y-1 text-sm">
                    {(detail.data?.sessions ?? []).slice(0, 5).map((s) => (
                      <li key={String(s.id)} className="text-[var(--text-secondary)]">
                        {String(s.device_id || "device")} · {formatDate(String(s.last_active))}
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <section className="space-y-2 lg:col-span-3">
                <p className="text-sm font-bold">Deliveries</p>
                {(detail.data?.deliveries ?? []).length === 0 ? (
                  <p className="text-sm text-[var(--text-muted)]">No assigned deliveries yet.</p>
                ) : (
                  <ul className="space-y-1 text-sm">
                    {(detail.data?.deliveries ?? []).slice(0, 10).map((d) => (
                      <li key={String(d.id)} className="text-[var(--text-secondary)]">
                        #{String(d.order_number || d.display_number || String(d.id).slice(0, 8))} · {String(d.client_name || "—")} ·{" "}
                        {String(d.status)} · {formatDate(String(d.completed_at || d.created_at))}
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          ) : null}
        </AdminPanel>
      )}

      <AdminPanel
        className="mt-4"
        toolbar={
          <div className="flex w-full flex-wrap items-center gap-2">
            <SearchBar
              value={search}
              onChange={(e) => {
                setPage(1);
                setSearch(e.target.value);
              }}
              placeholder="Search driver, client, or partner…"
              className="admin-search"
            />
            <SegmentStrip
              ariaLabel="Driver fleet"
              value={fleetFilter}
              onChange={(key) => {
                setPage(1);
                setFleetFilter(key);
              }}
              options={[
                { key: "", label: "All fleets", icon: Users, count: summary?.total },
                { key: "oelox_network", label: "OELOX global", icon: Globe2, count: summary?.network },
                { key: "partner", label: "Partner drivers", icon: Handshake, count: summary?.partner },
                { key: "restaurant", label: "Client drivers", icon: Store, count: summary?.client ?? summary?.restaurant },
              ]}
            />
          </div>
        }
      >
        {items.length === 0 && !drivers.isLoading ? (
          <EmptyState
            icon={<Search className="h-5 w-5" />}
            title="No drivers found"
            body="Create an OELOX global driver, or wait for clients and partners to onboard their own fleets."
            actionLabel="Add OELOX global driver"
            actionIcon={<Plus className="h-4 w-4" />}
            onAction={openCreate}
          />
        ) : (
          <div className="admin-table-wrap">
            <TableFlat
              columns={[
                {
                  key: "display_name",
                  header: "Driver",
                  render: (row) => (
                    <div className="admin-row-main">
                      <span className="admin-row-avatar admin-row-avatar--accent" aria-hidden>
                        {driverLabel(row).slice(0, 1).toUpperCase()}
                      </span>
                      <div className="min-w-0">
                        <p className="admin-row-title">{driverLabel(row)}</p>
                        <p className="admin-row-sub">{String(row.phone || row.driver_phone || "—")}</p>
                      </div>
                    </div>
                  ),
                },
                {
                  key: "fleet_type",
                  header: "Fleet",
                  render: (row) => (
                    <span
                      className={statusPillClass(
                        row.fleet_type === "oelox_network"
                          ? "online"
                          : row.fleet_type === "partner"
                            ? "trial"
                            : "offline"
                      )}
                    >
                      {fleetOwnershipLabel(row.fleet_type)}
                    </span>
                  ),
                },
                {
                  key: "is_active",
                  header: "Activation",
                  render: (row) => (
                    <span className={statusPillClass(row.is_active ? "online" : "offline")}>
                      {row.fleet_type === "oelox_network" && !row.is_active
                        ? "Pending code"
                        : row.is_active
                          ? "Activated"
                          : "Inactive"}
                    </span>
                  ),
                },
                {
                  key: "owner",
                  header: "Owner / home",
                  render: (row) => {
                    if (row.fleet_type === "partner") {
                      return String(row.partner_name || "Partner fleet");
                    }
                    if (row.fleet_type === "oelox_network") {
                      return "OELOX global";
                    }
                    return String(row.client_name || row.restaurant_name || "Client");
                  },
                },
                {
                  key: "active_links",
                  header: "Client links",
                  render: (row) => formatNumber(Number(row.active_links ?? 0)),
                },
                {
                  key: "collects_cod",
                  header: "COD",
                  render: (row) => (row.collects_cod ? "Yes" : "No"),
                },
                {
                  key: "is_online",
                  header: "Presence",
                  render: (row) => (
                    <span className={statusPillClass(row.is_online ? "online" : "offline")}>
                      {row.is_online ? "Online" : "Offline"}
                    </span>
                  ),
                },
                {
                  key: "actions",
                  header: "Actions",
                  render: (row) => (
                    <div className="flex flex-wrap gap-1.5">
                      <Button
                        type="button"
                        variant="secondary"
                        className="btn-sm sa-soft sa-soft--info"
                        icon={<Eye className="h-3.5 w-3.5" />}
                        onClick={() => setDetailDriverId(row.id)}
                      >
                        View
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        className="btn-sm sa-soft sa-soft--accent"
                        icon={<Pencil className="h-3.5 w-3.5" />}
                        onClick={() => openEdit(row.id)}
                      >
                        Edit
                      </Button>
                      {row.fleet_type === "oelox_network" ? (
                        <Button
                          type="button"
                          variant="secondary"
                          className="btn-sm sa-soft sa-soft--accent"
                          icon={<KeyRound className="h-3.5 w-3.5" />}
                          disabled={mintMut.isPending}
                          onClick={() => mintMut.mutate(row.id)}
                        >
                          {row.activated_at ? "New code" : "Code"}
                        </Button>
                      ) : null}
                      <Button
                        type="button"
                        variant="secondary"
                        className={`btn-sm sa-soft ${row.is_active ? "sa-soft--warn" : "sa-soft--success"}`}
                        icon={<Power className="h-3.5 w-3.5" />}
                        disabled={activeMut.isPending}
                        onClick={() => {
                          if (row.is_active) setDeactivateTarget(row.id);
                          else activeMut.mutate({ id: row.id, active: true });
                        }}
                      >
                        {row.is_active ? "Deactivate" : "Activate"}
                      </Button>
                      {row.fleet_type === "oelox_network" ? (
                        <Button
                          type="button"
                          variant="secondary"
                          className="btn-sm sa-soft sa-soft--info"
                          icon={<Link2 className="h-3.5 w-3.5" />}
                          onClick={() => setLinkDriverId(row.id)}
                        >
                          Link
                        </Button>
                      ) : null}
                    </div>
                  ),
                },
                {
                  key: "created_at",
                  header: "Joined",
                  render: (row) => (
                    <span className="text-[var(--text-muted)]">
                      {formatDate(String(row.created_at))}
                    </span>
                  ),
                },
              ]}
              data={items}
              keyField="id"
            />
          </div>
        )}
      </AdminPanel>

      <AdminPager
        page={page}
        totalPages={totalPages}
        total={drivers.data?.total ?? 0}
        label="drivers"
        onPrev={() => setPage((p) => p - 1)}
        onNext={() => setPage((p) => p + 1)}
      />

      <FormModal
        open={showCreate}
        onClose={closeCreate}
        title={activationCode ? "Activation code" : "Create OELOX global driver"}
        subtitle={
          activationCode
            ? "Share this one-time code with the driver now. It will not be shown again."
            : "Provisions a platform OELOX global driver. They activate once in the Driver app — not a client or partner invite."
        }
        eyebrow="OELOX global"
        icon={<Bike className="w-5 h-5" />}
        footer={
          activationCode ? (
            <div className="flex flex-wrap justify-end gap-2">
              <Button
                type="button"
                onClick={async () => {
                  await navigator.clipboard.writeText(activationCode);
                  setCopied(true);
                  toast.success("Activation code copied");
                }}
              >
                {copied ? "Copied" : "Copy activation code"}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setShowCreate(false);
                  setActivationCode(null);
                  setCopied(false);
                }}
              >
                Done
              </Button>
            </div>
          ) : (
            <div className="flex flex-wrap justify-end gap-2">
              <Button type="button" variant="ghost" onClick={closeCreate}>
                Cancel
              </Button>
              <Button
                type="button"
                loading={createMut.isPending}
                onClick={() => {
                  const missing = missingCreateFields();
                  if (missing.length) {
                    const message = `Required: ${missing.join(", ")}`;
                    setFormError(message);
                    toast.error(message);
                    return;
                  }
                  createMut.mutate();
                }}
              >
                {createMut.isPending ? "Saving…" : "Create & generate activation code"}
              </Button>
            </div>
          )
        }
      >
        {activationCode ? (
          <div className="space-y-3 p-1">
            <p className="font-mono text-3xl tracking-[0.35em] font-black text-[var(--text-primary)]">
              {activationCode}
            </p>
            <p className="text-sm text-[var(--text-secondary)]">
              The driver enters this code once in the Driver app to activate. Login after that is session-only.
            </p>
          </div>
        ) : (
          <div className="space-y-3 p-1">
            <div className="grid gap-3 md:grid-cols-2">
              <label className="admin-field">
                <span className="admin-field__label">Full name *</span>
                <input
                  className="input-base"
                  placeholder="Driver name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
              <label className="admin-field">
                <span className="admin-field__label">Phone (unique) *</span>
                <input
                  className="input-base"
                  placeholder="e.g. 0750 123 4567"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </label>
              <label className="admin-field">
                <span className="admin-field__label">Vehicle type *</span>
                <select
                  className="input-base"
                  value={vehicleType}
                  onChange={(e) => setVehicleType(e.target.value)}
                >
                  <option value="motorcycle">Motorcycle</option>
                  <option value="car">Car</option>
                  <option value="bicycle">Bicycle</option>
                </select>
              </label>
              <label className="admin-field">
                <span className="admin-field__label">Plate / vehicle number *</span>
                <input
                  className="input-base"
                  placeholder="Plate number"
                  value={vehicleNumber}
                  onChange={(e) => setVehicleNumber(e.target.value)}
                />
              </label>
              <label className="admin-field">
                <span className="admin-field__label">Vehicle model</span>
                <input
                  className="input-base"
                  placeholder="Optional"
                  value={vehicleModel}
                  onChange={(e) => setVehicleModel(e.target.value)}
                />
              </label>
              <label className="flex items-end gap-2 pb-2 text-sm font-semibold">
                <input
                  type="checkbox"
                  checked={collectsCod}
                  onChange={(e) => setCollectsCod(e.target.checked)}
                />
                Collects COD
              </label>
              <FileField label="IC image" required file={icFile} onChange={setIcFile} />
              <FileField label="License image" required file={licenseFile} onChange={setLicenseFile} />
              <div className="md:col-span-2">
                <FileField
                  label="Home ID / address proof"
                  file={homeIdFile}
                  onChange={setHomeIdFile}
                />
              </div>
            </div>
            {formError && <p className="text-sm text-red-600">{formError}</p>}
          </div>
        )}
      </FormModal>

      <FormModal
        open={!!linkDriverId}
        onClose={() => {
          setLinkDriverId(null);
          setLinkTenantId("");
        }}
        title="Link OELOX global driver to client"
        subtitle="Adds a client business to this global driver’s serve list. Does not convert them into a client-owned driver."
        eyebrow="OELOX global"
        icon={<Bike className="w-5 h-5" />}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setLinkDriverId(null);
                setLinkTenantId("");
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!linkTenantId}
              loading={linkMut.isPending}
              onClick={() => linkMut.mutate()}
            >
              Link client
            </Button>
          </div>
        }
      >
        <label className="admin-field p-1">
          <span className="admin-field__label">Client business</span>
          <select
            className="input-base"
            value={linkTenantId}
            onChange={(e) => setLinkTenantId(e.target.value)}
          >
            <option value="">Select client…</option>
            {tenantOptions.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name || t.id}
              </option>
            ))}
          </select>
        </label>
      </FormModal>

      <FormModal
        open={!!editDriverId}
        onClose={() => setEditDriverId(null)}
        title="Edit driver"
        subtitle="Name, contact, vehicle and flags. Fleet ownership and client links are managed from the driver list."
        eyebrow="Drivers"
        icon={<Pencil className="w-5 h-5" />}
        panelClassName="max-w-3xl"
        footer={
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setEditDriverId(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!editForm.name.trim() || !editForm.phone.trim()}
              loading={editMut.isPending}
              onClick={() => editMut.mutate()}
            >
              {editMut.isPending ? "Saving…" : "Save changes"}
            </Button>
          </div>
        }
      >
        <div className="grid gap-3 p-1 md:grid-cols-2">
          <label className="admin-field">
            <span className="admin-field__label">Full name *</span>
            <input
              className="input-base"
              value={editForm.name}
              onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
            />
          </label>
          <label className="admin-field">
            <span className="admin-field__label">Phone *</span>
            <input
              className="input-base"
              value={editForm.phone}
              onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))}
            />
          </label>
          <label className="admin-field">
            <span className="admin-field__label">Email</span>
            <input
              className="input-base"
              value={editForm.email}
              onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
            />
          </label>
          <label className="admin-field">
            <span className="admin-field__label">Vehicle type</span>
            <select
              className="input-base"
              value={editForm.vehicle_type}
              onChange={(e) => setEditForm((f) => ({ ...f, vehicle_type: e.target.value }))}
            >
              <option value="motorcycle">Motorcycle</option>
              <option value="car">Car</option>
              <option value="bicycle">Bicycle</option>
            </select>
          </label>
          <label className="admin-field">
            <span className="admin-field__label">Plate / number</span>
            <input
              className="input-base"
              value={editForm.vehicle_number}
              onChange={(e) => setEditForm((f) => ({ ...f, vehicle_number: e.target.value }))}
            />
          </label>
          <label className="admin-field">
            <span className="admin-field__label">Model</span>
            <input
              className="input-base"
              value={editForm.vehicle_model}
              onChange={(e) => setEditForm((f) => ({ ...f, vehicle_model: e.target.value }))}
            />
          </label>
          <label className="admin-field">
            <span className="admin-field__label">Color</span>
            <input
              className="input-base"
              value={editForm.vehicle_color}
              onChange={(e) => setEditForm((f) => ({ ...f, vehicle_color: e.target.value }))}
            />
          </label>
          <label className="admin-field md:col-span-2">
            <span className="admin-field__label">Address</span>
            <input
              className="input-base"
              value={editForm.address}
              onChange={(e) => setEditForm((f) => ({ ...f, address: e.target.value }))}
            />
          </label>
          <label className="flex items-center gap-2 text-sm font-semibold">
            <input
              type="checkbox"
              checked={editForm.collects_cod}
              onChange={(e) => setEditForm((f) => ({ ...f, collects_cod: e.target.checked }))}
            />
            Collects COD
          </label>
          <label className="flex items-center gap-2 text-sm font-semibold">
            <input
              type="checkbox"
              checked={editForm.is_verified}
              onChange={(e) => setEditForm((f) => ({ ...f, is_verified: e.target.checked }))}
            />
            KYC verified
          </label>
        </div>
      </FormModal>

      <FormModal
        open={!!deactivateTarget}
        onClose={() => setDeactivateTarget(null)}
        title="Deactivate driver?"
        subtitle="Soft delete — reversible anytime from this list. History, settlements and records are kept."
        eyebrow="Drivers"
        icon={<Power className="w-5 h-5" />}
        footer={
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setDeactivateTarget(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              loading={activeMut.isPending}
              onClick={() => {
                if (!deactivateTarget) return;
                activeMut.mutate({ id: deactivateTarget, active: false });
                setDeactivateTarget(null);
              }}
            >
              Deactivate
            </Button>
          </div>
        }
      >
        <p className="p-1 text-sm text-[var(--text-secondary)]">
          The driver will not receive orders or go online. Reactivate anytime — nothing is deleted.
        </p>
      </FormModal>
    </div>
  );
}
